import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { PracticalInfoIcon } from '../../../../common/practicalInfo';
import GetGeneratedPracticalInfoDescriptionQuery from './GetGeneratedPracticalInfoDescriptionQuery';
import UI from '../../../../common/UI';
import useLazyQuery from '../../../../api/useLazyQuery';
import useProject from '../../../useProject';

export interface GeneratePracticalInfoModalOptions {
  event: Event;
  topic: string;
  topicLabel: string;
  currentValue?: string | null;
  defaultContext: string;
  onGenerated: (description: string) => void;
}

const useGeneratePracticalInfoModal = ({
  event, topic, topicLabel, currentValue, defaultContext, onGenerated,
}: GeneratePracticalInfoModalOptions) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal maxWidth={600} close={closeModal}>
        <GeneratePracticalInfoForm
          event={event}
          topic={topic}
          topicLabel={topicLabel}
          currentValue={currentValue}
          defaultContext={defaultContext}
          onGenerated={onGenerated}
          onClose={() => closeModal()}
        />
      </UI.Modal>
    ),
    [event, topic, topicLabel, defaultContext, onGenerated],
  );

  return [openModal, closeModal];
};

interface GeneratePracticalInfoFormProps extends GeneratePracticalInfoModalOptions {
  onClose: () => void;
}

const GeneratePracticalInfoForm = ({
  event, topic, topicLabel, currentValue, defaultContext, onGenerated, onClose,
}: GeneratePracticalInfoFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const form = useForm({
    values: {
      context: defaultContext,
    },
  });

  const [generate, { loading, error }] = useLazyQuery(GetGeneratedPracticalInfoDescriptionQuery);

  const handleSubmit = async () => {
    const result = await generate({
      variables: {
        project_id: project.id,
        input: {
          event_id: event.id,
          topic,
          context: form.values.context,
          current_value: currentValue,
        },
      },
    });

    const description = result.data?.project.generated_practical_info_description.description;

    if (!description) {
      return;
    }

    onGenerated(description);
    onClose();
  };

  return (
    <UI.Form onSubmit={handleSubmit}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>
            {t('generate_content')}
          </UI.H4>
          <UI.Label sc={{ mb: 0 }}>
            <UI.Icon>
              <PracticalInfoIcon topic={topic} />
            </UI.Icon>
            {' '}
            {topicLabel}
          </UI.Label>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup>
          <UI.InputLabel htmlFor="practical_info_context">
            {t('additional_context')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('additional_context_description')}
          </UI.InputDescription>
          <UI.TextArea {...form.textarea('context')} autoFocus id="practical_info_context" />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('generate')}
          loading={loading}
          showLoader
          enabled={!loading}
          onClose={onClose}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default useGeneratePracticalInfoModal;
